import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { TerminalType } from "../../../graphql/types"
import { ServiceOnlineOrderingStatus } from "./aggregatorForm/components"
import { MDash } from "../../../constants/symbols"
import { MultiSelectItem } from "../../uiKit/MultiSelect/MultiSelect"
import { pipeline } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import { uniq } from "ramda"
import { AppsNewestVersion, LocationsTableServiceItem } from "./types"
import { lt } from "semver"

export const downloadQRcodeUrl = (id: Maybe<string>, fileName: string) => {
    const doc = document

    if (!doc || !id) {
        return null
    }

    const canvas = doc.getElementById(id)

    if (!canvas) {
        return null
    }

    // @ts-expect-error
    if (canvas && canvas.toDataURL()) {
        // @ts-expect-error
        const pngUrl = canvas.toDataURL("image/png", 1).replace("image/png", "image/octet-stream")
        const downloadLink = doc.createElement("a")
        downloadLink.href = pngUrl
        downloadLink.download = `${fileName}.png`

        if (doc.body) {
            doc.body.appendChild(downloadLink)
            downloadLink.click()

            doc.body.removeChild(downloadLink)
        }
    }

    return null
}

export const formatOnlineOrderingStatus = (status: Maybe<ServiceOnlineOrderingStatus>): string => {
    switch (status) {
        case ServiceOnlineOrderingStatus.Pending:
            return "Pending"
        case ServiceOnlineOrderingStatus.Active:
            return "Active"
        case ServiceOnlineOrderingStatus.Paused:
            return "Paused"
        case ServiceOnlineOrderingStatus.PausedForDelivery:
            return "Paused For Delivery"
        case ServiceOnlineOrderingStatus.PausedForPickup:
            return "Paused For Pickup"
        default:
            return MDash
    }
}

// From priceMultiplier to percentage
export const getPriceMultiplierValue = (input: Maybe<number>): Maybe<number> => {
    if (!input) {
        return null
    }
    return Math.round((input - 1) * 100)
}

// From percentage to priceMultiplier
export const constructPriceMultiplierValue = (input: Maybe<number>): Maybe<number> => {
    if (!input) {
        return null
    }
    return 1 + input / 100
}

export const adjustSelectedAssignments = (
    data: MultiSelectItem[],
    item: MultiSelectItem,
    isSelected: boolean,
    selectedIds: string[],
    onlyClicked: boolean
): string[] => {
    const newSelectedIds = isSelected ? uniq([...selectedIds, item.id]) : selectedIds.filter(id => id !== item.id)
    const childrenIds = item.children ? item.children.map(c => c.id) : null
    const parentItem = data.find(s => s.children?.some(a => a.id === item.id)) ?? null

    if (onlyClicked) {
        // If section was selected via "only" click, select all assignments in that section and deselect others
        if (isSelected && childrenIds) {
            return pipeline(childrenIds, (ids: string[]) => [...ids, item.id], uniq)
        }
        // If child item was selected, and it is only child, select the item + select parent
        if (isSelected && parentItem?.children?.length === 1) {
            return [item.id, parentItem.id]
        }
        // If child item was selected, and parent has multiple children, select just the item
        if (isSelected && (parentItem?.children?.length ?? 0) > 1) {
            return [item.id]
        }
    }

    // If section is selected, select all assignments in that section
    if (isSelected && childrenIds) {
        return pipeline(newSelectedIds, (ids: string[]) => [...ids, ...childrenIds], uniq)
    }

    // If section is deselected, deselect all assignments in that section
    if (!isSelected && childrenIds) {
        return newSelectedIds.filter(id => !childrenIds.includes(id))
    }

    // If all assignments in section are selected, select section
    if (isSelected && parentItem?.children?.every(a => newSelectedIds.includes(a.id))) {
        return pipeline(newSelectedIds, (ids: string[]) => [...ids, parentItem.id], uniq)
    }

    // If some assignment is unselected, unselect parent item
    if (!isSelected && parentItem) {
        return newSelectedIds.filter(id => id !== parentItem.id)
    }

    return newSelectedIds
}

export const isAppOutdated = (record: LocationsTableServiceItem, newestVersions: AppsNewestVersion): boolean => {
    const currentVersion = record.appVersion
    const newestVersion = getAppNewestVersion(record.type, newestVersions)

    if (!currentVersion || !newestVersion) {
        return false
    }

    return lt(currentVersion, newestVersion)
}

const getAppNewestVersion = (type: TerminalType, newestVersions: AppsNewestVersion) => {
    switch (type) {
        case TerminalType.Register:
            return newestVersions.Register
        case TerminalType.Kds:
            return newestVersions.KDS
        case TerminalType.Kiosk:
            return newestVersions.Kiosk
        default:
            return null
    }
}

export const locationAddressFormFields = {
    addressLine1: "addressLine1",
    addressLine2: "addressLine2",
    addressZip: "addressZip",
    addressCity: "addressCity",
    addressState: "addressState",
    googlePlaceId: "googlePlaceId",
    latitude: "latitude",
    longitude: "longitude",
}

export const canDeactivateTerminal = (terminalType: TerminalType) =>
    [
        TerminalType.Register,
        TerminalType.Kiosk,
        TerminalType.Kds,
        TerminalType.Aggregator,
        TerminalType.Pos,
        TerminalType.Osb,
        TerminalType.Mpos,
    ].includes(terminalType)
